import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Input,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete'; // Added DeleteIcon for remove functionality
import { styled } from '@mui/material/styles';

export interface FileUploadProps {
  open: boolean;
  onFileUploadClose: () => void;
  onFileUploadSubmit: (file: File, onProgress?: (progress: number) => void) => Promise<{
    success: boolean;
    message: string;
    data?: unknown;
  }>;
  acceptedFileTypes?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    overflow: 'hidden',
  },
}));

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.grey[400]}`,
  borderRadius: 12,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragging ? "#1976d28a" : '#f8fafc',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: '#f8fafc',
  borderRadius: 8,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const FileUpload: React.FC<FileUploadProps> = ({
  open,
  onFileUploadClose,
  onFileUploadSubmit,
  acceptedFileTypes,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showProgress, setShowProgress] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const dropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog is closed
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setFileContent(null);
      setImagePreview(null);
      setIsDragging(false);
      setUploadProgress(0);
      setShowProgress(false);
      setSnackbar({ open: false, message: '', severity: 'success' });
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [open]);

  const isFileTypeAccepted = useCallback(
    (file: File) => {
      if (!acceptedFileTypes) return true;
      const allowed = acceptedFileTypes
        .split(',')
        .map((ext) => ext.trim().toLowerCase());
      const fileName = file.name.toLowerCase();
      return allowed.some((ext) => fileName.endsWith(ext));
    },
    [acceptedFileTypes],
  );

  // Handle file selection and read content
  const handleFileUploadChange = useCallback((file: File | null) => {
    if (file && !isFileTypeAccepted(file)) {
      setSnackbar({
        open: true,
        message: `Invalid file type. Only ${acceptedFileTypes} files are allowed.`,
        severity: 'error',
      });
      setSelectedFile(null);
      setFileContent(null);
      setImagePreview(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    setFileContent(null);
    setImagePreview(null);
    setUploadProgress(0);
    setShowProgress(false);

    if (file) {
      const reader = new FileReader();
      if (file.type.startsWith('image/')) {
        reader.onload = (e) => setImagePreview(e.target?.result as string);
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('text/') || file.type === 'application/json') {
        reader.onload = (e) => setFileContent(e.target?.result as string);
        reader.readAsText(file);
      } else {
        setFileContent('Preview not available for this file type.');
      }
    }
  }, [acceptedFileTypes, isFileTypeAccepted]);
  // Handle remove file
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setFileContent(null);
    setImagePreview(null);
    setUploadProgress(0);
    setShowProgress(false);
    if (inputRef.current) {
      inputRef.current.value = ''; // Reset the file input
    }
    setSnackbar({
      open: true,
      message: 'File removed successfully',
      severity: 'success',
    });
  }, []);

  // Handle drag and drop events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] || null;
    if (file) handleFileUploadChange(file);
  }, [handleFileUploadChange]);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      handleFileUploadChange(file);
    },
    [handleFileUploadChange],
  );

  // Progress callback function
  const handleProgress = useCallback((progress: number) => {
    setUploadProgress(progress);
    setShowProgress(true);
  }, []);

  const handleFileUploadSubmitAction = useCallback(async () => {
    if (selectedFile) {
      setIsLoading(true);
      setUploadProgress(0);
      setShowProgress(true);

      try {
        const result = await onFileUploadSubmit(selectedFile, handleProgress);
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.success ? 'success' : 'error',
        });

        if (result.success) {
          setUploadProgress(100);
          setTimeout(() => {
            onFileUploadClose();
          }, 1000);
        } else {
          setShowProgress(false);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to upload file',
          severity: 'error',
        });
        setShowProgress(false);
      } finally {
        setIsLoading(false);
      }
    }
  }, [selectedFile, onFileUploadSubmit, onFileUploadClose, handleProgress]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && selectedFile && !isLoading) {
        handleFileUploadSubmitAction();
      }
    },
    [handleFileUploadSubmitAction, selectedFile, isLoading],
  );

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onFileUploadClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="file-upload-dialog-title"
        aria-describedby="file-upload-dialog-description"
      >
        <DialogTitle
          id="file-upload-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'white',
            py: 1,
            px: 3,
            mb: 5,
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          }}
        >
          Upload File
          <IconButton
            aria-label="Close dialog"
            onClick={onFileUploadClose}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'rotate(90deg)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4, bgcolor: 'background.paper' }} id="file-upload-dialog-description">
          <DropZone
            ref={dropRef}
            isDragging={isDragging}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            role="region"
            aria-label="File upload drop zone"
          >
            <Input
              type="file"
              onChange={handleFileInputChange}
              inputRef={inputRef}
              fullWidth
              disableUnderline
              sx={{ display: 'none' }}
              id="file-upload-input"
              inputProps={{ accept: acceptedFileTypes, 'aria-label': 'Select file to upload' }}
            />
            <label htmlFor="file-upload-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={isLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  py: 1,
                  px: 4,
                  borderRadius: 8,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                Select File
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              or drag and drop here
            </Typography>
            {selectedFile && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: 'success.main' }} aria-hidden="true" />
                <Typography variant="body1" fontWeight="medium" color="text.primary">
                  {selectedFile.name}
                </Typography>
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                  sx={{
                    color: 'error.main',
                    // '&:hover': {
                    //   bgcolor: 'error.light',
                    //   transform: 'scale(1.1)',
                    //   transition: 'all 0.3s ease',
                    // },
                  }}
                  aria-label="Remove selected file"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </DropZone>

          {/* Progress Bar */}
          {showProgress && (
            <ProgressContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.primary" fontWeight="medium">
                  Upload Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: uploadProgress === 100 ? 'success.main' : 'primary.main',
                    transition: 'background-color 0.3s ease',
                  },
                }}
              />
              {uploadProgress === 100 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                  <Typography variant="body2" color="success.main" fontWeight="medium">
                    Upload completed successfully!
                  </Typography>
                </Box>
              )}
            </ProgressContainer>
          )}

          {imagePreview && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Preview
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  display: 'inline-block',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <img
                  src={imagePreview}
                  alt="File preview"
                  style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px' }}
                />
              </Box>
            </Box>
          )}

          {/* {fileContent && (
            <Box sx={{ mt: 3, maxHeight: '250px', overflowY: 'auto', bgcolor: 'grey.100', p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                File Content
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 1 }}>
                {fileContent}
              </Typography>
            </Box>
          )} */}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, bgcolor: 'background.paper' }}>
          <Button
            onClick={onFileUploadClose}
            variant="outlined"
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              px: 3,
              borderRadius: 8,
              color: 'text.secondary',
              borderColor: 'grey.400',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'grey.100',
                transform: 'translateY(-2px)',
              },
            }}
            aria-label="Cancel upload"
          >
            Cancel
          </Button>
          <Button
            onClick={handleFileUploadSubmitAction}
            variant="contained"
            disabled={!selectedFile || isLoading}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              px: 3,
              borderRadius: 8,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.400',
                color: 'grey.600',
              },
            }}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            aria-label="Upload file"
          >
            {isLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </StyledDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
          aria-live="polite"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

FileUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  onFileUploadClose: PropTypes.func.isRequired,
  onFileUploadSubmit: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.string,
};

export default FileUpload;